import { type FormEvent, useState } from "react";
import MicButton from "./MicButton";
import PromptsSheet from "./PromptsSheet";
import FeedbackSheet from "./FeedbackSheet";
import CreditsSheet from "./CreditsSheet";
import { useSessionStore } from "../store/sessionStore";
import type { PipelineStatus } from "../types";

interface InputBarProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (question: string) => void;
  micStart: () => void | Promise<void>;
  micStop: () => void;
  micRecording: boolean;
  /** The mic was tapped but iOS isn't capturing yet — don't speak yet. */
  micStarting?: boolean;
  voiceEnabled?: boolean;
  disabled?: boolean;
  /** Disables only the mic; defaults to ``disabled`` (the candidate mic fixes keep it usable while an answer streams). */
  micDisabled?: boolean;
  status: PipelineStatus;
}

export default function InputBar({ value, onChange, onSubmit, micStart, micStop, micRecording, micStarting, voiceEnabled, disabled, micDisabled, status }: InputBarProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const sessionId = useSessionStore((s) => s.sessionId);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = value.trim();
    if (!q) return;
    onSubmit(q);
    onChange("");
  };

  const isBusy = disabled && status !== "idle";
  const statusLabel: Partial<Record<PipelineStatus, string>> = {
    listening: "Listening…",
    transcribing: "Transcribing…",
    classifying: "Thinking…",
    fetching: "Fetching data…",
    narrating: "Composing…",
    playing: "Playing…",
    error: "Something went wrong",
  };

  const hasStatusLine = isBusy && !!statusLabel[status];
  return (
    <div className="flex-shrink-0 px-3 sm:px-4 pb-2 pt-1 bg-scout-bg">
      <div className="max-w-chat mx-auto">
        {/* Status pill — role="status" makes it a live region so screen readers
            announce state changes (Listening, Thinking, etc.) without
            requiring focus.  The element stays in the DOM so screen readers
            keep watching, but it collapses to zero height when there's no
            active status to avoid wasting vertical space above the input. */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className={`px-3 flex items-center gap-1.5 overflow-hidden transition-all ${
            hasStatusLine ? "h-6 mb-1.5" : "h-0 mb-0"
          }`}
        >
          {hasStatusLine && (
            <>
              <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full animate-pulse ${
                status === "error" ? "bg-red-500" :
                status === "listening" ? "bg-red-400" :
                status === "playing" ? "bg-purple-400" : "bg-scout-yellow"
              }`} />
              <span className={`text-xs font-medium ${
                status === "error" ? "text-red-400" : "text-white/50"
              }`}>
                {statusLabel[status]}
              </span>
            </>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex items-center gap-2 glass rounded-2xl px-3 py-2 focus-within:border-white/50 focus-within:outline-none transition-colors"
        >
          {voiceEnabled && (
            <MicButton onStart={micStart} onStop={micStop} isRecording={micRecording} isStarting={micStarting} disabled={micDisabled ?? disabled} />
          )}

          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={voiceEnabled && micStarting ? "Getting the mic ready…" : voiceEnabled && micRecording ? "Listening… speak now" : "Ask Scout about any Pokémon…"}
            disabled={disabled}
            className="flex-1 bg-transparent text-white text-sm outline-none placeholder-white/25 disabled:opacity-60 min-w-0"
            aria-label="Question input"
          />

          {/* Prompts help button */}
          <button
            type="button"
            data-tour-id="prompts"
            onClick={() => setSheetOpen(true)}
            aria-label="Show example prompts"
            title="What can I ask?"
            className="flex-shrink-0 w-11 h-11 rounded-xl bg-white/[0.06] flex items-center justify-center text-white/40 hover:text-white/80 hover:bg-white/[0.10] transition-colors"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <circle cx="12" cy="17" r="0.5" fill="currentColor" />
            </svg>
          </button>

          <button
            type="submit"
            disabled={disabled || !value.trim()}
            aria-label="Ask"
            className="flex-shrink-0 w-11 h-11 rounded-xl bg-scout-blue flex items-center justify-center text-white disabled:opacity-30 hover:bg-blue-600 transition-colors"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
              <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
            </svg>
          </button>
        </form>

        {/* Disclaimer + sources + feedback link.
            The "Not affiliated" line satisfies Niantic's ToS requirement
            to preserve trademark notices in fan content.  The "Sources"
            button opens the credits sheet that lists every data
            provider (PvPoke / PoGoAPI / Bulbapedia / LeekDuck /
            ScrapedDuck / pokemongo.com news) with its licence — see
            CreditsSheet.tsx for the detail. */}
        <p className="text-center text-[10px] text-white/20 mt-1.5 select-none">
          Not affiliated with Niantic or The Pokémon Company.
          {" · "}
          <button
            type="button"
            onClick={() => setCreditsOpen(true)}
            className="text-white/30 hover:text-white/70 underline-offset-2 hover:underline transition-colors"
          >
            Sources
          </button>
          {sessionId && (
            <>
              {" · "}
              <button
                type="button"
                onClick={() => setFeedbackOpen(true)}
                className="text-white/30 hover:text-white/70 underline-offset-2 hover:underline transition-colors"
              >
                Send feedback
              </button>
            </>
          )}
        </p>

        <PromptsSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          onSelect={(prompt) => {
            setSheetOpen(false);
            onSubmit(prompt);
          }}
        />

        {sessionId && (
          <FeedbackSheet
            open={feedbackOpen}
            onClose={() => setFeedbackOpen(false)}
            sessionId={sessionId}
          />
        )}

        <CreditsSheet
          open={creditsOpen}
          onClose={() => setCreditsOpen(false)}
        />
      </div>
    </div>
  );
}


